<template>
  <div
    ref="contentEle"
    :style="{
      outline: 'none',
      position: 'relative',
      width: (lineTo - lineFrom) * 1.7 + 'em',
      top: 1 + (contentProp.tag_type == 'togaki' ? 6 : 4) + 'em',
      height: oneLine + 3 + 'em',
    }"
    contenteditable="true"
    @beforeinput="beforeInputHandler"
    @compositionend="compositionEndHandler"
    @compositionupdate="compositionUpdateHandler"
    @compositionstart="compositionStartHandler"
    :data-contentid="contentProp.uuid"
    :id="contentProp.uuid"
    @input="inputHandler"
  >
    <div
      v-for="word in contentArray"
      :style="{
        position: 'absolute',
        right: word.right,
        top: word.top,
        'background-color': backgroundColor(word),
        'text-decoration': word.isTorikeshiLine ? 'line-through' : '',
        color: word.isSetTextColor ? textColor : '',
        'white-space': 'pre-wrap',
      }"
      :data-index="word.index"
    >
      {{ word.word }}
    </div>
  </div>
</template>

<script>
import 'babel-polyfill';
import http_method from '../mixins/http_method';
import announce_message from '../mixins/announce_message';
import content_method from '../mixins/content_method';
import redo_undo_method from '../mixins/redo_undo_method';
import caret_method from '../mixins/caret_method';
import set_range_method from '../mixins/set_range_method';
import hardware_action_method from '../mixins/hardware_action_method';
import 'element-plus';
import * as CONSTS from '../constants.js';
import Content from './../classes/content.js';
import { nextTick } from 'vue';
import { graphemeCount, graphemesOf, sliceGraphemes, codeUnitToGrapheme } from '../text_segment.js';

export default {
  name: 'BasicSentence',
  data() {
    return {
      togakiOneLine: CONSTS.TOGAKI_MAX_COUNT,
      serifuOneLine: CONSTS.SERIFU_MAX_COUNT,
      serifuFirstLine: CONSTS.FIRST_SERIFU_MAX_COUNT,
      compositionStartOffset: 0,
      targetDiv: null,
      isComposingEnd: false,
      beforeCompositionText: '',
      // IME変換中はこのコンテンツで検索ハイライトを一時停止するフラグ。
      // 変換中はupdatingbodyに全角スペースのプレースホルダが挿入され、body基準の
      // searchWordStartIndex/currentSearch.indexと位置がズレる。ズレたハイライトが
      // 変換中のDOMノードにbackground-color変更を与え続けると、macOSのライブ変換が
      // 壊れて入力文字や周辺の本文が消えるため、変換中はハイライトを無効化する。
      isComposingContent: false,
    };
  },
  props: {
    contentProp: {
      type: Content,
      default: '',
    },
    lineFrom: {
      type: Number,
      default: 0,
    },
    lineTo: {
      type: Number,
      default: 0,
    },
    contentBody: {
      type: String,
      default: '',
    },
    searchWord: {
      type: String,
      default: '',
    },
    currentSearch: {
      type: Object,
      default: {},
    },
    selectFrom: {
      type: Object,
      default: {},
    },
    selectTo: {
      type: Object,
      default: {},
    },
    textColor: {
      type: String,
      default: '',
    },
    isComposing: {
      type: Boolean,
      default: false,
    },
    enterKeyPressed: {
      type: Boolean,
      default: false,
    },
    isMouseDown: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['composing-start', 'change', 'search-refresh'],
  computed: {
    // 検索ワードの書記素数（表示上の文字数）。CU数(searchWord.length)の代替。
    searchWordLength() {
      return graphemeCount(this.searchWord);
    },
    oneLine() {
      switch (this.contentProp.tag_type) {
        case 'togaki':
          return this.togakiOneLine;
        case 'serifu':
          return this.serifuOneLine;
        default:
          return 0;
      }
    },
    // 変換中の文字が乗っているdivのindex（compositionStartHandlerのtargetDiv算出と同じ式）
    // prop変更等による再レンダーでも常にこのindexのtopを変換中用の値に保つために使う
    composingIndex() {
      return this.isComposingContent ? Math.max(0, this.compositionStartOffset - 1) : null;
    },
    contentArray() {
      if (this.contentBody || true) {
        if (this.contentBody == '<br>') {
          this.contentProp.updateBody('');
        }
        if (this.contentProp.updatingbody == '') {
          // 本文が空の状態から変換を開始する場合、通常のmapループを通らずここで返るため、
          // ここでも変換中用のtopオーバーライドを適用する
          const emptyTop = this.composingIndex === 0 ? CONSTS.COMPOSING_TOP : '0em';
          return [
            {
              index: 0,
              top: emptyTop,
              right: '0em',
              word: '',
              isSelected: this.contentProp.selectFrom <= 0 && 0 <= this.contentProp.selectTo,
            },
          ];
        }
        let wordCount = 0;
        let lineFixed = false;
        let searchItemStartIndexArray = [];
        // 1行に検索ワードが2つ以上ある場合の対応（書記素基準）
        if (this.contentProp?.itemsCount > 1) {
          searchItemStartIndexArray.push(this.contentProp?.searchWordStartIndex);
          const swLen = this.searchWordLength;
          let sliceContentBody = sliceGraphemes(this.contentBody, this.contentProp?.searchWordStartIndex + swLen);
          // 削られた文字の数
          let sliceWordCount = this.contentProp?.searchWordStartIndex;
          for (let i = 1; i < this.contentProp?.itemsCount; i++) {
            const cu = sliceContentBody.indexOf(this.searchWord);
            const searchWordIndex = cu < 0 ? -1 : codeUnitToGrapheme(sliceContentBody, cu);
            // 削るたびに更新
            sliceWordCount += searchWordIndex + swLen;
            searchItemStartIndexArray.push(sliceWordCount);
            sliceContentBody = sliceGraphemes(sliceContentBody, searchWordIndex + swLen);
          }
        }

        return graphemesOf(this.contentBody)
          .map((word, index) => {
            let top = '';
            let right = '';
            let isSelected = this.contentProp.selectFrom <= index && index <= this.contentProp.selectTo;
            let lineNum = 0;
            let isSearched = false;
            let isCurrentSearched = false;
            let isTorikeshiLine = false;
            let isSetTextColor = false;
            if (this.contentProp.tag_type == 'togaki') {
              if (
                wordCount % this.oneLine == 0 &&
                ['。', '、', '」', '』', '）'].includes(word) &&
                !lineFixed &&
                index != 0
              ) {
                lineNum = Math.floor(wordCount / this.oneLine) - 1;
                top = 1.12 * this.oneLine + 'em';
                right = 1.7 * (lineNum - this.lineFrom) + 'em';
                lineFixed = true;
              } else {
                lineNum = Math.floor(wordCount / this.oneLine);
                top = 1.12 * (wordCount % this.oneLine) + 'em';
                right = 1.7 * (lineNum - this.lineFrom) + 'em';
                lineFixed = false;
                wordCount++;
              }
            } else if (this.contentProp.tag_type == 'serifu') {
              if (
                wordCount >= this.serifuFirstLine &&
                (wordCount - this.serifuFirstLine) % this.oneLine == 0 &&
                ['。', '、', '」', '』', '）'].includes(word) &&
                !lineFixed &&
                index != 0
              ) {
                lineNum = Math.floor(wordCount / this.oneLine) - 1;
                top = 1.12 * this.oneLine + 'em';
                right = 1.7 * (lineNum - this.lineFrom) + 'em';
                lineFixed = true;
              } else {
                if (wordCount < this.serifuFirstLine) {
                  lineNum = Math.floor(wordCount / this.serifuFirstLine);
                  top = 1.12 * ((wordCount % this.serifuFirstLine) - 4) + 'em';
                  right = 1.7 * (lineNum - this.lineFrom) + 'em';
                } else {
                  lineNum = 1 + Math.floor((wordCount - this.serifuFirstLine) / this.oneLine);
                  top = 1.12 * ((wordCount - this.serifuFirstLine) % this.oneLine) + 'em';
                  right = 1.7 * (lineNum - this.lineFrom) + 'em';
                }
                lineFixed = false;
                wordCount++;
              }
            }
            // 変換中はVueの再レンダー（IME確定と無関係なprop変更等）で手動設定した
            // topが確定後の値に巻き戻ることがあるため、computed側でも常に正しいtopを返す
            if (this.composingIndex === index) {
              top = CONSTS.COMPOSING_TOP;
            }
            // 検索時背景色変更
            // 変換中はupdatingbodyにプレースホルダが入りbody基準のindexとズレるため、
            // ハイライト計算をスキップして変換中DOMへのbackground-color変更を防ぐ
            if (!this.isComposingContent) {
              if (
                this.contentProp.uuid == this.currentSearch.content?.uuid &&
                this.currentSearch.index <= index &&
                index < this.currentSearch.index + this.searchWordLength
              ) {
                isCurrentSearched = true;
              } else {
                if (this.contentProp?.itemsCount > 1) {
                  // 1行に検索ワードが2つ以上ある場合の対応
                  for (let i = 0; i < searchItemStartIndexArray.length; i++) {
                    if (
                      Number(searchItemStartIndexArray[i]) <= index &&
                      index < Number(searchItemStartIndexArray[i]) + this.searchWordLength
                    ) {
                      isSearched = true;
                    }
                  }
                } else {
                  if (this.contentProp?.searchWordStartIndex != null) {
                    if (
                      this.contentProp?.searchWordStartIndex <= index &&
                      index < this.contentProp?.searchWordStartIndex + this.searchWordLength
                    ) {
                      isSearched = true;
                    }
                  }
                }
              }
            }
            // style適用
            for (let i = 0; i < this.contentProp.style.length; i++) {
              if (this.contentProp.style[i].start_index <= index && index <= this.contentProp.style[i].end_index) {
                if (!isTorikeshiLine) {
                  isTorikeshiLine = this.contentProp.style[i].style_type == 'torikeshi_line' ? true : false;
                }
                if (!isSetTextColor) {
                  isSetTextColor = this.contentProp.style[i].style_type == 'text_color' ? true : false;
                }
              }
            }
            if (lineNum >= this.lineFrom && lineNum < this.lineTo) {
              return {
                index: index,
                top: top,
                right: right,
                word: word,
                isSelected: isSelected,
                isSearched: isSearched,
                isCurrentSearched: isCurrentSearched,
                isTorikeshiLine: isTorikeshiLine,
                isSetTextColor: isSetTextColor,
              };
            } else {
              return null;
            }
          })
          .filter(Boolean);
      } else {
        [''];
      }
    },
  },
  methods: {
    async compositionStartHandler(event) {
      const selection = window.getSelection();

      // 現在のカーソル位置を取得
      const focusNode = selection.focusNode;
      const focusElement = focusNode?.tagName ? focusNode : focusNode?.parentNode;
      // focusNodeが文字divそのものとは限らない（コンテナ要素等の場合）ため、data-indexを持つ
      // 最も近い祖先要素まで遡って探索する（初回コンポジションでcompositionStartOffsetが
      // 誤ったindexになりtargetDivが見つからない問題の対策）
      const currentDiv = focusElement?.closest?.('[data-index]') || this.$refs.contentEle.children[0];

      // 正しいcompositionStartOffsetを計算
      if (this.contentProp.updatingbody == '') {
        this.compositionStartOffset = 0;
      } else {
        // 通常のケース：現在のページ内
        // offset 0: 要素の前（index位置）、offset 1: 要素の後（index+1位置）
        // 1書記素=1divなので focusOffset(CU) は 0 かその書記素のCU長 → 0/1 の書記素オフセットに写像
        this.compositionStartOffset = Number(currentDiv.dataset.index) + (selection.focusOffset > 0 ? 1 : 0);
      }

      this.targetDiv = this.$refs.contentEle.querySelectorAll(
        `[data-index="${Math.max(0, this.compositionStartOffset - 1)}"]`,
      )[0];

      if (!this.targetDiv) return;

      // 変換中は検索ハイライトを停止（ズレたハイライトによる変換DOMの破壊を防ぐ）
      this.isComposingContent = true;

      // IME表示位置を調整
      if (this.contentProp.tag_type == 'togaki') {
        this.targetDiv.style['text-indent'] =
          ((Math.max(0, this.compositionStartOffset - 1) -
            this.contentProp.countProtrusion(this.compositionStartOffset)) %
            CONSTS.TOGAKI_MAX_COUNT) *
            1.12 +
          'em';
        this.targetDiv.style.height = this.oneLine + 3 + 'em';
        this.targetDiv.style.top = CONSTS.COMPOSING_TOP;
      } else if (this.contentProp.tag_type == 'serifu') {
        this.targetDiv.style.height = this.oneLine * 1.12 + 'em';
        this.targetDiv.style.top = CONSTS.COMPOSING_TOP;
        if (this.compositionStartOffset < this.serifuFirstLine) {
          this.targetDiv.style['text-indent'] =
            ((Math.max(0, this.compositionStartOffset - 1) -
              this.contentProp.countProtrusion(this.compositionStartOffset) -
              4) %
              this.serifuFirstLine) *
              1.12 +
            'em';
        } else {
          this.targetDiv.style['text-indent'] =
            ((Math.max(0, this.compositionStartOffset - 1) -
              this.contentProp.countProtrusion(this.compositionStartOffset) -
              4) %
              this.oneLine) *
              1.12 +
            'em';
        }
      }

      this.beforeCompositionText = this.targetDiv.innerText;

      // 同じページ内でのカーソル移動（IMEセッションは継続される）
      const range = document.createRange();
      range.setStart(this.targetDiv, this.compositionStartOffset > 0 ? 1 : 0);
      range.setEnd(this.targetDiv, this.compositionStartOffset > 0 ? 1 : 0);
      selection.removeAllRanges();
      selection.addRange(range);

      this.$emit('composing-start');
    },
    async compositionUpdateHandler(event) {
      let beforeUpdateDataLength = this.contentProp.graphemeLength;
      this.contentProp.updatingbody =
        sliceGraphemes(this.contentProp.body, 0, this.compositionStartOffset) +
        '　'.repeat(graphemeCount(event.data)) +
        sliceGraphemes(this.contentProp.body, this.compositionStartOffset);
      let updateGap = this.contentProp.graphemeLength - beforeUpdateDataLength;
      this.contentProp.style.forEach((style) => {
        if (style.start_index + 1 > this.compositionStartOffset) style.start_index = style.start_index + updateGap;
        if (style.end_index + 1 >= this.compositionStartOffset) style.end_index = style.end_index + updateGap;
      });
      // updatingbody変更によるcontentArrayの再描画でtargetDivのtopが確定位置に巻き戻ることがあるため、
      // input イベント（inputHandler）を待たずここでも補正する（複数コードユニット文字の仮確定で
      // compositionupdate が input より後に発火するケースに対応）
      await nextTick(() => {
        if (this.targetDiv) this.targetDiv.style.top = CONSTS.COMPOSING_TOP;
      });
    },
    async compositionEndHandler(event) {
      // TODO: ライブ変換に対応するためにライブ変換以外のIME操作（スペースキーでの変換、確定前の変換内容の変更など）も正しく処理する必要がある
      // 完全終了の判定
      const isCompleteEnd = this.isMouseDown || this.enterKeyPressed || event.data.length === 0;

      this.$emit('change');
      // 正しい位置（compositionStartOffset）にテキストを挿入
      let serifuOffset = this.contentProp.updateBody(
        sliceGraphemes(this.contentProp.body, 0, this.compositionStartOffset) +
          event.data +
          sliceGraphemes(this.contentProp.body, this.compositionStartOffset),
      );
      this.contentProp.style.forEach((style) => {
        if (style.start_index + 1 > this.compositionStartOffset) style.start_index = style.start_index + serifuOffset;
        if (style.end_index + 1 >= this.compositionStartOffset) style.end_index = style.end_index + serifuOffset;
      });

      // targetDivのスタイルをリセット
      if (this.targetDiv) {
        this.targetDiv.style.height = null;
        this.targetDiv.style.top = null;
        this.targetDiv.style['text-indent'] = null;
        if (this.beforeCompositionText != '') {
          // 台詞の場合、先頭にスペースが入るため、スペースを考慮してテキストを復元
          if (
            this.targetDiv.dataset.index == 0 &&
            this.contentProp.tag_type == 'serifu' &&
            this.contentProp.body[0] === '　'
          ) {
            this.targetDiv.innerText = '　';
          } else {
            this.targetDiv.innerText = this.beforeCompositionText;
          }
        }
      }

      await nextTick(() => {
        const selection = document.getSelection();
        const range = document.createRange();

        // 挿入後のカーソル位置を計算
        if (event.data.length === 0) {
          // 空文字の場合（全削除された場合）
          // compositionStartOffsetの位置にカーソルを配置
          if (this.compositionStartOffset === 0 && this.contentProp.bodyGraphemeLength === 0) {
            // 完全に空の場合
            range.setStart(this.$refs.contentEle, 0);
            range.setEnd(this.$refs.contentEle, 0);
            selection.removeAllRanges();
            selection.addRange(range);
          } else if (this.compositionStartOffset === 0) {
            // 先頭の場合
            let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${this.contentProp.uuid}"]`);
            let nextFocusDiv = null;
            nextFocusContentElements.forEach((ele) => {
              let selectorArray = ele.querySelectorAll(`[data-index="0"]`);
              if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
            });
            if (nextFocusDiv) {
              range.setStart(nextFocusDiv, 0);
              range.setEnd(nextFocusDiv, 0);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          } else {
            // 途中の場合
            let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${this.contentProp.uuid}"]`);
            let nextFocusDiv = null;
            nextFocusContentElements.forEach((ele) => {
              let selectorArray = ele.querySelectorAll(`[data-index="${this.compositionStartOffset - 1}"]`);
              if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
            });
            if (nextFocusDiv) {
              range.setStart(nextFocusDiv, 1);
              range.setEnd(nextFocusDiv, 1);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        } else {
          // 通常の挿入の場合
          const newCursorIndex = this.compositionStartOffset + graphemeCount(event.data) + serifuOffset - 1;

          // 全てのページから正しい要素を探す
          let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${this.contentProp.uuid}"]`);
          let nextFocusDiv = null;
          nextFocusContentElements.forEach((ele) => {
            let selectorArray = ele.querySelectorAll(`[data-index="${Math.max(0, newCursorIndex)}"]`);
            if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
          });

          if (nextFocusDiv) {
            const offset = newCursorIndex >= 0 && event.data.length > 0 ? 1 : 0;
            range.setStart(nextFocusDiv, offset);
            range.setEnd(nextFocusDiv, offset);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      });

      // 変換確定後に検索ハイライトを再開
      this.isComposingContent = false;
      // 編集したこのコンテンツだけ検索情報を再計算してもらう
      this.$emit('search-refresh', this.contentProp);
    },
    async beforeInputHandler(event) {
      if (!event.isComposing) {
        // keydown時のカーソル位置とコンテンツ情報を記録
        const selection = window.getSelection();
        const targetDiv = selection.focusNode?.dataset?.contentid
          ? selection.focusNode.children[0]
          : selection.focusNode?.dataset?.index
          ? selection.focusNode
          : selection.focusNode.parentNode;
        if (!this.$refs.contentEle.contains(targetDiv) || event.data === null) {
          event.preventDefault();
          return;
        }
        this.$emit('change');
        const offset = selection.focusOffset > 0 ? 1 : 0;
        const targetContent = this.contentMap.get(this.contentProp.uuid);

        // 1文字=1要素なので、挿入位置を正しく計算
        // offset=0: 要素の前（index位置）、offset=1: 要素の後（index+1位置）
        let insertIndex;
        if (targetContent.bodyGraphemeLength == 0) {
          insertIndex = 0;
        } else {
          insertIndex = Number(targetDiv.dataset.index) + offset;
        }
        // スペース２回入力でなぜかピリオドが含まれてしまう問題の対応
        const insertData = event.data == '. ' ? ' ' : event.data;
        const insertLen = graphemeCount(insertData);
        const newBody =
          sliceGraphemes(this.contentProp.body, 0, insertIndex) +
          insertData +
          sliceGraphemes(this.contentProp.body, insertIndex);

        targetContent.updateBody(newBody); // 変更をトリガーするためにupdateBodyを呼び出す
        this.contentProp.style.forEach((style) => {
          if (style.start_index + 1 > insertIndex) style.start_index = style.start_index + insertLen;
          if (style.end_index + 1 >= insertIndex) style.end_index = style.end_index + insertLen;
        });

        event.preventDefault();
        // カーソルを挿入位置の後ろに移動（1文字=1要素、offset 1）
        await nextTick(() => {
          const range = document.createRange();
          let nextFocusContentElements = document.querySelectorAll(`[data-contentid="${this.contentProp.uuid}"]`);
          let nextFocusDiv = null;

          nextFocusContentElements.forEach((ele) => {
            let selectorArray = ele.querySelectorAll(`[data-index="${insertIndex + insertLen - 1}"]`);
            if (selectorArray.length == 1) nextFocusDiv = selectorArray[0];
          });

          if (nextFocusDiv) {
            range.setStart(nextFocusDiv, 1);
            range.setEnd(nextFocusDiv, 1);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        });

        // 半角入力（直接入力）時も編集したこのコンテンツだけ検索情報を再計算してもらう
        this.$emit('search-refresh', this.contentProp);
      }
    },
    async inputHandler(event) {
      if (event.isComposing) {
        if (!this.isComposing) {
          this.$emit('composing-start');
        }
      }

      await nextTick(async () => {
        if (this.isComposingContent && this.targetDiv) this.targetDiv.style.top = CONSTS.COMPOSING_TOP;
      });
    },
    // 背景色変更
    backgroundColor(word) {
      if (word.isCurrentSearched) {
        return word.isSelected ? '#91bbd3' : '#6dd58c';
      }
      if (word.isSearched) {
        return word.isSelected ? '#9ecbe5' : '#c4eed1';
      }
      return word.isSelected ? '#b4d7ff' : null;
    },
  },
  mixins: [
    http_method,
    announce_message,
    content_method,
    redo_undo_method,
    caret_method,
    set_range_method,
    hardware_action_method,
  ],
  created() {},
};
</script>
